import React, { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Paperclip, X } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";
import { uploadFile } from "@/lib/upload";

export default function NewExpense() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [form, setForm] = useState({
    expense_date: new Date().toISOString().slice(0, 10),
    category: "",
    hub: "",
    vendor_name: "",
    bill_number: "",
    amount: "",
    remarks: "",
    receipt_file: null,
    receipt_name: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/categories").then(({ data }) => {
      setCats(data);
      if (data[0]) setForm((f) => ({ ...f, category: data[0].name }));
    });
    api.get("/hubs").then(({ data }) => {
      setHubs(data);
      setForm((f) => ({
        ...f,
        hub: f.hub || user?.hub || (data[0] ? data[0].name : ""),
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.hub]);

  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("File must be under 5MB");
      return;
    }
    setError("");
    setForm({ ...form, receipt_file: file, receipt_name: file.name });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    setUploadPct(0);
    try {
      let receipt_file_id = null;
      if (form.receipt_file) {
        setUploadPct(1);
        const up = await uploadFile(form.receipt_file, setUploadPct);
        receipt_file_id = up.file_id;
      }
      await api.post("/expenses", {
        expense_date: form.expense_date,
        category: form.category,
        hub: form.hub,
        vendor_name: form.vendor_name,
        bill_number: form.bill_number,
        amount: parseFloat(form.amount),
        remarks: form.remarks,
        receipt_file_id,
        receipt_name: form.receipt_name || null,
      });
      toast.success("Bill submitted for manager review");
      nav("/expenses");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
      setUploadPct(0);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Submit</div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-900">New expense bill</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Flow: Submitted → Manager Review → Approved → Finance Payment → Paid.
        </p>

        <form onSubmit={submit} className="mt-6 bg-white border border-zinc-200 rounded-lg p-6 space-y-4" data-testid="new-expense-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="expdate">Expense date</Label>
              <Input id="expdate" data-testid="new-date" type="date" required
                value={form.expense_date} onChange={set("expense_date")} className="mt-1 font-mono" />
            </div>
            <div>
              <Label>Hub name</Label>
              <Select value={form.hub} onValueChange={set("hub")}>
                <SelectTrigger data-testid="new-hub" className="mt-1">
                  <SelectValue placeholder="Choose hub" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {hubs.map((h) => (
                    <SelectItem key={h.id} value={h.name}>{h.name}</SelectItem>
                  ))}
                  {form.hub && !hubs.some((h) => h.name === form.hub) && (
                    <SelectItem value={form.hub}>{form.hub}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expense category</Label>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger data-testid="new-category" className="mt-1">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {cats.map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="amount">Amount (₹)</Label>
              <Input id="amount" data-testid="new-amount" type="number" step="0.01" min="0.01" required
                value={form.amount} onChange={set("amount")} className="mt-1 font-mono" />
            </div>
            <div>
              <Label htmlFor="vendor">Vendor / Owner name</Label>
              <Input id="vendor" data-testid="new-vendor" required
                value={form.vendor_name} onChange={set("vendor_name")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="billno">Bill number</Label>
              <Input id="billno" data-testid="new-billno" required
                value={form.bill_number} onChange={set("bill_number")} className="mt-1 font-mono" />
            </div>
          </div>

          <div>
            <Label htmlFor="remarks">Remarks</Label>
            <Textarea id="remarks" data-testid="new-remarks" rows={3}
              value={form.remarks} onChange={set("remarks")} placeholder="Any notes for the reviewer" className="mt-1" />
          </div>

          <div>
            <Label>Bill upload (image or PDF, ≤ 5MB)</Label>
            <div className="mt-1 flex items-center gap-3">
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-md text-sm hover:bg-zinc-50 transition-colors duration-200">
                <Paperclip size={16} />
                <span>Attach bill</span>
                <input data-testid="new-receipt" type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} />
              </label>
              {form.receipt_name && (
                <div className="text-xs text-zinc-600 flex items-center gap-1.5">
                  <span className="font-mono">{form.receipt_name}</span>
                  <button type="button"
                    onClick={() => setForm({ ...form, receipt_file: null, receipt_name: "" })}
                    className="text-zinc-400 hover:text-rose-600 transition-colors duration-200">
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div data-testid="new-expense-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <Button type="submit" disabled={saving} data-testid="submit-expense-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving
                ? (uploadPct > 0 && uploadPct < 100 ? `Uploading bill ${uploadPct}%…` : "Submitting…")
                : "Submit bill"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => nav(-1)} data-testid="cancel-expense-btn">Cancel</Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
